import { House, List, MagnifyingGlass, MapPin, Plus, type Icon } from '@phosphor-icons/react'
import type { CSSProperties } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/design-system'
import { useT } from '@/i18n'
import { useCan } from '@/stores/auth'
import { useUi } from '@/stores/ui'
import { useTabLink } from './workspace/tabMeta'

type Slot = { key: string; to?: string; end?: boolean; icon: Icon; label: string; create?: boolean }

/** Which slot the page belongs to: the menu while it is open, else the link the page sits under (as NavLink matches). */
export function activeSlot(slots: Pick<Slot, 'key' | 'to' | 'end'>[], pathname: string, menuOpen: boolean): number {
  if (menuOpen) return slots.findIndex((s) => s.key === 'menu')
  return slots.findIndex((s) => s.to && (s.end ? pathname === s.to : pathname === s.to || pathname.startsWith(`${s.to}/`)))
}

/**
 * The phone's bottom bar: a floating pill above the home indicator (owner, 2026-10-30: «Floating Pill Bottom
 * Navigation with Animated Active Tab»). One soft pill glides behind the active tab; the create button sits in the
 * middle as a red gem. Hidden from md up (the sidebar is there).
 */
export function MobileTabBar() {
  const { locale } = useT()
  const ar = locale === 'ar'
  const setMobileNav = useUi((s) => s.setMobileNav)
  const menuOpen = useUi((s) => s.mobileNavOpen)
  const canCreate = useCan('shipments:write')
  const { pathname } = useLocation()
  // each shortcut opens (or brings back) its own tab instead of replacing the current page
  const tabLink = useTabLink()
  const slots: Slot[] = [
    { key: 'home', to: '/my', end: true, icon: House, label: ar ? 'الرئيسية' : 'Home' },
    { key: 'shipments', to: '/reports/shipment-details', icon: MagnifyingGlass, label: ar ? 'الشحنات' : 'Shipments' },
    ...(canCreate ? [{ key: 'create', to: '/operations/shipments/new', icon: Plus, label: ar ? 'شحنة جديدة' : 'New shipment', create: true }] : []),
    { key: 'tracking', to: '/reports/awb-tracking', icon: MapPin, label: ar ? 'التتبع' : 'Tracking' },
    { key: 'menu', icon: List, label: ar ? 'القائمة' : 'Menu' },
  ]
  const active = activeSlot(slots, pathname, menuOpen)
  const glide = active >= 0 && !slots[active]!.create
  return (
    <nav className="tabbar md:hidden" aria-label={ar ? 'التنقل السريع' : 'Quick navigation'} style={{ '--slots': slots.length, '--at': Math.max(active, 0) } as CSSProperties}>
      <span aria-hidden className={cn('tabbar-glider', glide && 'is-on')} />
      {slots.map((s, i) => {
        const on = i === active
        const face = (
          <>
            <s.icon size={s.create ? 24 : 22} weight={s.create ? 'bold' : on ? 'fill' : 'regular'} />
            {!s.create && <span>{s.label}</span>}
          </>
        )
        if (s.create) {
          return (
            <NavLink key={s.key} to={s.to!} {...tabLink(s.to!)} className={cn('tabbar-create', on && 'is-active')} aria-label={s.label}>
              <span>{face}</span>
            </NavLink>
          )
        }
        if (!s.to) {
          return (
            <button key={s.key} type="button" className={cn('tabbar-item', on && 'is-active')} aria-expanded={menuOpen} onClick={() => setMobileNav(true)}>
              {face}
            </button>
          )
        }
        return (
          <NavLink key={s.key} to={s.to} end={s.end} {...tabLink(s.to)} className={cn('tabbar-item', on && 'is-active')} aria-current={on ? 'page' : undefined}>
            {face}
          </NavLink>
        )
      })}
    </nav>
  )
}
